import React, { useState, FormEvent, useEffect } from 'react';
import { SignupDetails, LoginDetails } from '../utils/auth';

type AuthMode = 'login' | 'signup';

interface AuthModalProps {
  mode: AuthMode;
  onClose: () => void;
  onSignup: (details: SignupDetails) => void;
  onLogin: (details: LoginDetails) => void;
  onSwitchMode: (mode: AuthMode) => void;
  errorMessage: string;
  pendingConfirmation: { email: string; } | null;
}

const AuthModal: React.FC<AuthModalProps> = ({ 
  mode, 
  onClose, 
  onSignup,
  onLogin,
  onSwitchMode,
  errorMessage,
  pendingConfirmation,
}) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Reset fields when mode changes or modal is re-opened
    setUsername('');
    setEmail('');
    setPassword('');
  }, [mode, pendingConfirmation]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'signup') {
      onSignup({ username, email, password });
    } else {
      onLogin({ email, password });
    }
  };

  if (pendingConfirmation) {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-navy-blue p-8 rounded-lg shadow-2xl w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
           <button onClick={onClose} className="absolute top-4 right-4 text-light-blue hover:text-off-white text-2xl">&times;</button>
          <h2 className="text-2xl font-bold text-center text-off-white mb-4">Confirm Your Account</h2>
          <p className="text-center text-light-blue mb-6">
            A confirmation link has been sent to <span className="font-bold text-off-white">{pendingConfirmation.email}</span>. Please click the link in the email to activate your account.
          </p>
           <div className="bg-dark-blue p-4 rounded-lg text-center">
             <p className="text-light-blue">You can close this window.</p>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-navy-blue p-8 rounded-lg shadow-2xl w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-light-blue hover:text-off-white text-2xl">&times;</button>
        <h2 className="text-3xl font-bold text-center text-off-white mb-6">
          {mode === 'login' ? 'Sign In' : 'Create Account'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-light-blue mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full bg-dark-blue border border-steel-blue rounded-lg p-3 text-off-white focus:ring-btc-orange focus:border-btc-orange"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-light-blue mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={mode === 'login' ? 'Enter your email' : 'Enter your email'}
              className="w-full bg-dark-blue border border-steel-blue rounded-lg p-3 text-off-white focus:ring-btc-orange focus:border-btc-orange"
            />
          </div>
          

          <div>
            <label className="block text-sm font-medium text-light-blue mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full bg-dark-blue border border-steel-blue rounded-lg p-3 text-off-white focus:ring-btc-orange focus:border-btc-orange"
            />
          </div>
          
          {errorMessage && <p className="text-red-400 text-sm text-center bg-red-900/50 p-2 rounded-lg">{errorMessage}</p>}

          <button type="submit" className="w-full bg-btc-orange text-dark-blue font-bold py-3 px-4 rounded-lg hover:bg-orange-400 transition-colors duration-300">
            {mode === 'login' ? 'Sign In' : 'Sign Up'}
          </button>
        </form>

        <p className="text-center text-light-blue mt-6">
          {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
          <button onClick={() => onSwitchMode(mode === 'login' ? 'signup' : 'login')} className="font-bold text-btc-orange hover:underline ml-2">
             {mode === 'login' ? 'Sign Up' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default AuthModal;